import React, { useEffect, useState } from 'react'
import { Shield, Lock, Eye, FileText, Users, Database, Scale, AlertTriangle, FileCheck } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { useLocation } from 'react-router-dom'

type TabType = 'privacy' | 'terms'

export const LegalPage: React.FC = () => {
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<TabType>('privacy')

  useEffect(() => {
    // Set initial tab based on hash
    if (location.hash === '#terms') {
      setActiveTab('terms')
      // Scroll to terms section
      setTimeout(() => {
        const element = document.getElementById('terms')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    } else if (location.hash === '#privacy') {
      setActiveTab('privacy')
      // Scroll to privacy section
      setTimeout(() => {
        const element = document.getElementById('privacy')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [location.hash])

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-4">
              <Scale className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100 mb-4">Legal</h1>
            <p className="text-slate-600 dark:text-slate-400">
              Last Updated: January 1, 2025
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-8">
            <div className="flex border-b border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setActiveTab('privacy')}
                className={`flex-1 px-6 py-4 text-center font-medium transition-all duration-200 relative ${
                  activeTab === 'privacy'
                    ? 'text-[#69adba] border-b-2 border-[#69adba]'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Shield className="h-5 w-5" />
                  <span>Privacy Policy</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('terms')}
                className={`flex-1 px-6 py-4 text-center font-medium transition-all duration-200 relative ${
                  activeTab === 'terms'
                    ? 'text-[#69adba] border-b-2 border-[#69adba]'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <FileCheck className="h-5 w-5" />
                  <span>Terms of Service</span>
                </div>
              </button>
            </div>
          </div>

          {/* Privacy Policy Section */}
          {activeTab === 'privacy' && (
          <section id="privacy" className="mb-20 scroll-mt-20">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-4">
                <Shield className="h-8 w-8 text-[#69adba]" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-4">Privacy Policy</h2>
            </div>

            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
                At Open1031, we are committed to protecting your privacy and ensuring the security of your personal 
                information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you use our 1031 exchange platform and services.
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-12">
              {/* Information We Collect */}
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-lg flex items-center justify-center">
                    <Database className="h-6 w-6 text-[#69adba]" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Information We Collect</h3>
                </div>
                
                <div className="space-y-4 text-slate-700 dark:text-slate-300">
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Personal Information</h4>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Name, email address, phone number, and mailing address</li>
                      <li>Date of birth and Social Security Number (for identity verification and IRS reporting)</li>
                      <li>Government-issued identification documents (driver's license, passport)</li>
                      <li>Selfie photos for identity verification purposes</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Financial Information</h4>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Property sale details, purchase prices, and transaction information</li>
                      <li>Bank account information for fund transfers</li>
                      <li>Tax-related information including capital gains calculations</li>
                      <li>Investment accreditation status and documentation</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Usage Information</h4>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Device information, IP address, browser type, and operating system</li>
                      <li>Pages visited, features used, and time spent on the platform</li>
                      <li>Chat conversations with our AI assistant (Neela)</li>
                      <li>Documents uploaded to the platform</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* How We Use Your Information */}
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-lg flex items-center justify-center">
                    <FileText className="h-6 w-6 text-[#69adba]" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">How We Use Your Information</h3>
                </div>
                
                <div className="space-y-3 text-slate-700 dark:text-slate-300">
                  <p>We use the collected information for the following purposes:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Service Delivery:</strong> To facilitate your 1031 exchange transactions and provide qualified intermediary services</li>
                    <li><strong>Identity Verification:</strong> To verify your identity and comply with Know Your Customer (KYC) and Anti-Money Laundering (AML) regulations</li>
                    <li><strong>IRS Compliance:</strong> To prepare and file required IRS forms including Form 8824 (Like-Kind Exchanges)</li>
                    <li><strong>Communication:</strong> To send transaction updates, deadline reminders, and important notices about your exchange</li>
                    <li><strong>Platform Improvement:</strong> To analyze usage patterns and improve our AI assistant and user experience</li>
                    <li><strong>Security:</strong> To detect, prevent, and respond to fraud, security incidents, and other harmful activities</li>
                    <li><strong>Legal Obligations:</strong> To comply with legal requirements, court orders, and regulatory investigations</li>
                  </ul>
                </div>
              </div>

              {/* How We Share Your Information */}
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-[#69adba]" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">How We Share Your Information</h3>
                </div>
                
                <div className="space-y-3 text-slate-700 dark:text-slate-300">
                  <p>We may share your information with:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf (payment processing, identity verification, cloud hosting)</li>
                    <li><strong>Financial Institutions:</strong> Banks and escrow agents involved in your exchange transactions</li>
                    <li><strong>Government Agencies:</strong> IRS, SEC, and other regulatory bodies as required by law</li>
                    <li><strong>Real Estate Professionals:</strong> Brokers, title companies, and closing agents involved in your transactions</li>
                    <li><strong>Legal and Compliance:</strong> Law enforcement, courts, and legal counsel when required by law or to protect our rights</li>
                  </ul>
                  <p className="mt-4">
                    <strong>We do not sell your personal information to third parties for marketing purposes.</strong>
                  </p>
                </div>
              </div>

              {/* Data Security */}
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-lg flex items-center justify-center">
                    <Lock className="h-6 w-6 text-[#69adba]" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Data Security</h3>
                </div>
                
                <div className="space-y-3 text-slate-700 dark:text-slate-300">
                  <p>We implement robust security measures to protect your information:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                    <li><strong>Access Controls:</strong> Role-based access with multi-factor authentication for employees</li>
                    <li><strong>Regular Audits:</strong> Third-party security audits and penetration testing</li>
                    <li><strong>Compliance:</strong> SOC 2 Type II certified infrastructure</li>
                    <li><strong>Monitoring:</strong> 24/7 security monitoring and incident response</li>
                  </ul>
                </div>
              </div>

              {/* Your Rights */}
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-lg flex items-center justify-center">
                    <Eye className="h-6 w-6 text-[#69adba]" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Your Privacy Rights</h3>
                </div>
                
                <div className="space-y-3 text-slate-700 dark:text-slate-300">
                  <p>You have the following rights regarding your personal information:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                    <li><strong>Correction:</strong> Request corrections to inaccurate or incomplete information</li>
                    <li><strong>Deletion:</strong> Request deletion of your information (subject to legal retention requirements)</li>
                    <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                    <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                    <li><strong>Object:</strong> Object to processing of your information for certain purposes</li>
                  </ul>
                  <p className="mt-4">
                    To exercise these rights, contact us at <a href="mailto:privacy@open1031.ai" className="text-[#69adba] hover:text-[#5a9aa6] dark:text-[#69adba] dark:hover:text-[#7bb8c5]">privacy@open1031.ai</a>
                  </p>
                </div>
              </div>

              {/* Data Retention */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Data Retention</h3>
                <p className="text-slate-700 dark:text-slate-300">
                  We retain your information for as long as necessary to provide services and comply with legal obligations. 
                  For 1031 exchanges, we must retain transaction records for at least 7 years per IRS regulations. 
                  After this period, we securely delete or anonymize your information unless longer retention is required by law.
                </p>
              </div>

              {/* Cookies */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Cookies and Tracking</h3>
                <p className="text-slate-700 dark:text-slate-300 mb-3">
                  We use cookies and similar tracking technologies to improve your experience. You can control cookies through 
                  your browser settings. Note that disabling cookies may limit certain features of the platform.
                </p>
                <p className="text-slate-700 dark:text-slate-300">
                  We use essential cookies (for authentication and security), analytics cookies (to understand usage), 
                  and functionality cookies (to remember your preferences).
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Children's Privacy</h3>
                <p className="text-slate-700 dark:text-slate-300">
                  Open1031 is not intended for use by individuals under 18 years of age. We do not knowingly collect 
                  personal information from children. If you believe we have collected information from a child, 
                  please contact us immediately.
                </p>
              </div>

              {/* Changes to Policy */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Changes to This Policy</h3>
                <p className="text-slate-700 dark:text-slate-300">
                  We may update this Privacy Policy from time to time. We will notify you of material changes by email 
                  or through a prominent notice on the platform. Your continued use of Open1031 after changes constitutes 
                  acceptance of the updated policy.
                </p>
              </div>
            </div>
          </section>
          )}

          {/* Terms of Service Section */}
          {activeTab === 'terms' && (
          <section id="terms" className="mb-20 scroll-mt-20">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-4">
                <FileCheck className="h-8 w-8 text-[#69adba]" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-4">Terms of Service</h2>
            </div>

            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
                By using Open1031, you agree to these terms. Please read them carefully before using our platform.
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-12">
              {/* Use of Service */}
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-lg flex items-center justify-center">
                    <Shield className="h-6 w-6 text-[#69adba]" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Use of Service</h3>
                </div>
                
                <div className="space-y-3 text-slate-700 dark:text-slate-300">
                  <p>You are responsible for the accuracy of information you provide. When using Open1031:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You must provide accurate, current, and complete information</li>
                    <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                    <li>You agree not to use the service for any illegal or unauthorized purpose</li>
                    <li>You must be at least 18 years old to use the platform</li>
                    <li>You agree to notify us immediately of any unauthorized use of your account</li>
                  </ul>
                </div>
              </div>

              {/* Compliance */}
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-lg flex items-center justify-center">
                    <Scale className="h-6 w-6 text-[#69adba]" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Compliance</h3>
                </div>
                
                <div className="space-y-3 text-slate-700 dark:text-slate-300">
                  <p>Open1031 provides tools to assist with 1031 exchanges; final compliance remains your responsibility:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You are responsible for ensuring your exchange complies with all applicable IRS regulations</li>
                    <li>We recommend consulting with qualified tax professionals for complex transactions</li>
                    <li>You are responsible for meeting all deadlines and requirements for your exchange</li>
                    <li>We provide qualified intermediary services but cannot guarantee tax outcomes</li>
                    <li>You must comply with all applicable laws and regulations in your jurisdiction</li>
                  </ul>
                </div>
              </div>

              {/* Liability */}
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-[#69adba]" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Liability and Disclaimers</h3>
                </div>
                
                <div className="space-y-3 text-slate-700 dark:text-slate-300">
                  <p>Service is provided "as is" without warranties:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>We do not provide tax, legal, or financial advice</li>
                    <li>We are not responsible for decisions made based on information provided through the platform</li>
                    <li>We do not guarantee the accuracy, completeness, or timeliness of information</li>
                    <li>Our liability is limited to the fees paid for our services</li>
                    <li>We are not liable for indirect, incidental, or consequential damages</li>
                  </ul>
                </div>
              </div>

              {/* Intellectual Property */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Intellectual Property</h3>
                <p className="text-slate-700 dark:text-slate-300">
                  All content, features, and functionality of the Open1031 platform are owned by Open1031, Inc. 
                  and are protected by copyright, trademark, and other intellectual property laws. You may not 
                  reproduce, distribute, or create derivative works without our express written permission.
                </p>
              </div>

              {/* Termination */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Termination</h3>
                <p className="text-slate-700 dark:text-slate-300">
                  We reserve the right to suspend or terminate your account at any time for violation of these 
                  terms or for any other reason. You may terminate your account at any time by contacting us. 
                  Upon termination, your right to use the service will immediately cease.
                </p>
              </div>

              {/* Changes to Terms */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Changes to Terms</h3>
                <p className="text-slate-700 dark:text-slate-300">
                  We may update these Terms of Service from time to time. We will notify you of material changes 
                  by email or through a prominent notice on the platform. Your continued use of Open1031 after 
                  changes constitutes acceptance of the updated terms.
                </p>
              </div>
            </div>
          </section>
          )}

          {/* Contact */}
          <section className="border-t border-slate-200 dark:border-slate-700 pt-12">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Contact Us</h2>
            <p className="text-slate-700 dark:text-slate-300 mb-4">
              If you have questions about our legal policies, please contact us:
            </p>
            <div className="bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-xl p-6 space-y-2">
              <p className="text-slate-900 dark:text-slate-100"><strong>Email:</strong> <a href="mailto:legal@open1031.ai" className="text-[#69adba] hover:text-[#5a9aa6] dark:text-[#69adba] dark:hover:text-[#7bb8c5]">legal@open1031.ai</a></p>
              <p className="text-slate-900 dark:text-slate-100"><strong>Address:</strong> Open1031, Inc., 123 Exchange Street, Suite 100, San Francisco, CA 94105</p>
              <p className="text-slate-900 dark:text-slate-100"><strong>Phone:</strong> 1-800-OPEN-1031</p>
            </div>
          </section>
        </div>
      </div>

      <PublicFooter />
    </div>
  )
}

